import { yupResolver } from "@hookform/resolvers/yup";
import { EmailValidation } from "@krea/customer-features/form-validation/EmailValidation";
import { OrganisationValidation } from "@krea/customer-features/form-validation/OrganisationValidation";
import { MobilePhoneNumberValidation } from "@krea/customer-features/form-validation/PhoneNumberValidation";
import { useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import LayoutOneColumnHeader from "@krea/customer-features/shared-components/app/one-column/LayoutOneColumnHeader";
import FIBankIDFlow from "@krea/customer-features/shared-components/bankID-fi/FIBankIDFlow";
import BankIDFlow from "@krea/customer-features/shared-components/bankID/BankIDFlow";
import Button from "@krea/customer-features/shared-components/button";
import EmailInput from "@krea/customer-features/shared-components/loan-application-form/fields/email";
import OrganisationNumber from "@krea/customer-features/shared-components/loan-application-form/fields/organisation-number";
import PhoneInput from "@krea/customer-features/shared-components/loan-application-form/fields/phone";
import { Text } from "@krea/customer-features/shared-components/text";
import { APPLICATION_NAME, COUNTRY_CODE } from "@krea/customer-features/utils";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { ChangeEvent, useCallback, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useRegisterPartner } from "store/register/hooks/mutations/useRegisterPartner";

const getValidationSchema = () =>
  Yup.object().shape({
    organisation: OrganisationValidation(),
    email: EmailValidation(),
    mobile_phone_number: MobilePhoneNumberValidation(settings.countryCode),
  });

type RegisterFormType = Yup.InferType<ReturnType<typeof getValidationSchema>>;

export const RegisterPage = () => {
  const isMobile = useMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState(1);

  const { mutateAsync: registerPartner } = useRegisterPartner();
  const { control, getValues, formState, setValue, trigger } =
    useForm<RegisterFormType>({
      mode: "onChange",
      resolver: yupResolver(getValidationSchema()),
      defaultValues: {
        organisation: {
          name: "",
          organisationNumber: "",
        },
        email: "",
        mobile_phone_number: "",
      },
    });

  const organisationNumberController = useController({
    name: "organisation.organisationNumber",
    control,
    defaultValue: "",
  });

  const emailController = useController({
    name: "email",
    control,
  });
  const phoneNumberController = useController({
    name: "mobile_phone_number",
    control,
  });

  const isFormValid = () => {
    if (currentStep === 1) {
      const organisationNumber = organisationNumberController.field.value;

      const formValues = getValues();

      return (
        !!formValues.organisation.name &&
        !!organisationNumber &&
        !organisationNumberController.fieldState.invalid &&
        formValues.organisation.name !== organisationNumber
      );
    }

    return formState.isValid;
  };

  const formattedPostFormValues = useCallback(() => {
    const formValues = getValues();

    return {
      organisation_name: formValues.organisation.name,
      organisation_number: formValues.organisation.organisationNumber,
      email: formValues.email,
      mobile_phone_number: formValues.mobile_phone_number,
      country_code: settings.countryCode,
    };
  }, [getValues]);

  const onSuccess = async () => {
    await registerPartner(formattedPostFormValues());
  };

  return (
    <LayoutOneColumn>
      <LayoutOneColumnHeader
        title={t(`register.${currentStep === 3 ? "bankIDHeading" : "heading"}`)}
      >
        <Button variant="text" onClick={() => navigate("/login")}>
          {t("register.loginToExistingAccount")}
        </Button>
      </LayoutOneColumnHeader>

      <div className="tw-my-8">
        <Text size={!isMobile ? "md" : "sm"} className="tw-font-bold">
          {t("iaq.progressBar.steps", { currentStep, stepsQty: 3 })}
        </Text>

        <div className="tw-mt-3 tw-flex tw-justify-between tw-space-x-2">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={twMerge(
                "tw-w-full tw-h-[6px] md:tw-h-[8px] tw-bg-primary-150",
                {
                  "tw-bg-primary": currentStep >= step,
                },
              )}
            />
          ))}
        </div>
      </div>

      {currentStep < 3 ? (
        <>
          <h5>
            {t(`register.step${currentStep}`, {
              organisationName: getValues().organisation.name,
            })}
          </h5>

          <form
            className="tw-mb-6 tw-mt-3 tw-rounded tw-border tw-border-primary tw-p-6 tw-pt-8"
            onSubmit={(e) => e.preventDefault()}
          >
            {currentStep === 1 && (
              <OrganisationNumber
                value={organisationNumberController.field.value}
                error={organisationNumberController.fieldState.error?.message}
                label={t("register.companyNameLabel")!}
                touched={organisationNumberController.fieldState.isTouched}
                onBlur={organisationNumberController.field.onBlur}
                onCompanyChange={(company) => {
                  setValue(
                    "organisation.organisationNumber",
                    company.organisationNumber,
                    {
                      shouldTouch: true,
                      shouldDirty: true,
                      shouldValidate: true,
                    },
                  );
                  setValue("organisation.name", company.name, {
                    shouldTouch: true,
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                isLimitedCompany={false}
              />
            )}
            {currentStep === 2 && (
              <>
                <EmailInput
                  value={emailController.field.value}
                  label={t("register.emailLabel")}
                  error={emailController.fieldState.error?.message}
                  touched={emailController.fieldState.isTouched}
                  handleChange={(e: ChangeEvent<HTMLInputElement>) => {
                    emailController.field.onChange(e.target.value);
                  }}
                  onBlur={emailController.field.onBlur}
                />

                <PhoneInput
                  id="partner-register-phone"
                  value={phoneNumberController.field.value}
                  label={t("register.phoneLabel")}
                  error={phoneNumberController.fieldState.error?.message}
                  touched={phoneNumberController.fieldState.isTouched}
                  handleChange={(e: ChangeEvent<HTMLInputElement>) => {
                    phoneNumberController.field.onChange(e.target.value);
                  }}
                  onBlur={phoneNumberController.field.onBlur}
                />
              </>
            )}
          </form>
        </>
      ) : null}

      <div className="tw-flex tw-flex-col-reverse tw-justify-between lg:tw-flex-row-reverse">
        {currentStep < 3 ? (
          <Button
            onClick={() => setCurrentStep((step) => step + 1)}
            disabled={!isFormValid()}
          >
            {t("iaq.nextQuestionCta")}
          </Button>
        ) : null}

        {currentStep === 2 ? (
          <Button
            variant="text"
            onClick={() => setCurrentStep((step) => step - 1)}
          >
            {t("iaq.previousQuestionCta")}
          </Button>
        ) : null}
      </div>

      {currentStep === 3 ? (
        <>
          {settings.countryCode === COUNTRY_CODE.SE ? (
            <h5 className="tw-mb-10 tw-text-center">{t("register.step3")}</h5>
          ) : null}

          {settings.countryCode === COUNTRY_CODE.SE ? (
            <BankIDFlow
              authenticationContext={APPLICATION_NAME.CUSTOMER_WEB}
              onSuccess={onSuccess}
              onCancel={undefined}
              hideHeading={undefined}
            />
          ) : (
            <FIBankIDFlow
              // @ts-expect-error
              authenticationContext={APPLICATION_NAME.CUSTOMER_WEB}
              onSuccess={onSuccess}
              onCancel={() => navigate(0)}
            />
          )}
        </>
      ) : null}
    </LayoutOneColumn>
  );
};
